/**
 * Sync between a device and the user's server replica (specs/sync.md).
 *
 * Model: register-level last-writer-wins. A REGISTER is a set of columns of one row with one
 * hybrid-logical-clock column. The larger Hlc wins; equal Hlc = same write = no-op. Deletion is a
 * tombstone column inside a register. The server assigns every accepted register a sequence number;
 * devices pull "everything after my cursor".
 *
 * `SYNC_TABLES` below is the single source of truth for what syncs. WP0 generates the
 * dirty-marking triggers and the applier's column lists from it; a test asserts it matches
 * specs/schema.sql column for column.
 */

import type { DeviceId, Hlc } from './ids.js';
import { MODEL_SYNC_TABLES } from './models.js';
import type { SqlValue } from './storage.js';

/**
 * Data schema version (PRAGMA user_version): 1 = the first live v5, 2 = quick controls (per-folder source
 * statistics, first-harvest size), 3 = scoring models (models.md §4, §9.2). Push AND pull carry it; the
 * server refuses any other (sync.md §13).
 */
export const SCHEMA_VERSION = 3 as const;

export interface SyncRegisterSpec {
  /** 'row' for single-register tables. */
  readonly name: string;
  /** The column holding this register's Hlc. */
  readonly hlc: string;
  /** Data columns owned by the register (excluding key and hlc columns). */
  readonly columns: readonly string[];
}

export interface SyncTableSpec {
  readonly table: string;
  /** Primary-key columns. The wire id is their values joined with '|'. */
  readonly key: readonly string[];
  readonly registers: readonly SyncRegisterSpec[];
  /**
   * The message this row belongs to, if any: rows whose parent message is a stub are deleted
   * locally on every replica and their incoming changes are dropped (sync.md "Stubs").
   */
  readonly parentMessageColumn?: string;
}

const row = (columns: readonly string[]): readonly SyncRegisterSpec[] => [{ name: 'row', hlc: 'hlc', columns }];

/** Apply order matters only for readability; there are no foreign keys between synced tables. */
export const SYNC_TABLES = [
  { table: 'folders', key: ['id'], registers: row(['name', 'icon', 'description', 'builtin', 'is_keep', 'retention_days', 'position', 'created_at', 'deleted_at']) },
  { table: 'tags', key: ['id'], registers: row(['name', 'name_fold', 'icon', 'position', 'created_at', 'deleted_at']) },
  { table: 'topics', key: ['id'], registers: row(['name', 'hint', 'icon', 'color', 'enabled', 'disabled_reason', 'position', 'archived_at', 'created_at', 'deleted_at', 'phrases']) },
  { table: 'sources', key: ['id'], registers: row(['kind', 'name', 'locator', 'config', 'pull_minutes', 'format', 'keep_images', 'keep_videos', 'description', 'enabled', 'disabled_reason', 'archived_at', 'created_at', 'deleted_at', 'backfill_items']) },
  { table: 'source_days', key: ['source_id', 'day'], registers: row(['received', 'kept', 'folders', 'deleted']) },
  {
    table: 'messages',
    key: ['id'],
    registers: [
      { name: 'content', hlc: 'content_hlc', columns: ['source_id', 'external_id', 'received_at', 'published_at', 'sort_at', 'url', 'title', 'title_fold', 'author', 'author_fold', 'body_html', 'excerpt', 'image_url', 'media', 'word_count', 'reading_minutes', 'lang', 'thread_key', 'extra', 'content_state'] },
      { name: 'placement', hlc: 'placement_hlc', columns: ['folder_id', 'folder_entered_at', 'placed_by_kind', 'placed_by_id', 'auto_folder_id', 'auto_by_kind', 'auto_by_id'] },
      { name: 'read', hlc: 'read_hlc', columns: ['read_at'] },
      { name: 'auto', hlc: 'auto_hlc', columns: ['pipeline_state', 'filter_results', 'score_state', 'scores', 'best_score', 'best_topic_id', 'scored_at', 'scoring_model'] },
      { name: 'life', hlc: 'life_hlc', columns: ['deleted_at', 'delete_reason'] },
    ],
  },
  { table: 'message_topics', key: ['message_id', 'topic_id'], registers: row(['score', 'origin', 'hidden', 'deleted_at']), parentMessageColumn: 'message_id' },
  { table: 'message_tags', key: ['message_id', 'tag_id'], registers: row(['by_kind', 'by_id', 'deleted_at']), parentMessageColumn: 'message_id' },
  { table: 'events', key: ['id'], registers: row(['message_id', 'at', 'kind', 'actor_kind', 'actor_id', 'from_folder_id', 'to_folder_id', 'detail', 'deleted_at']), parentMessageColumn: 'message_id' },
  { table: 'corrections', key: ['id'], registers: row(['message_id', 'kind', 'from_folder_id', 'to_folder_id', 'topic_id', 'topic_change', 'auto_actor_kind', 'auto_actor_id', 'by_kind', 'by_id', 'snapshot', 'status', 'note', 'created_at', 'resolved_at', 'deleted_at']) },
  { table: 'rules', key: ['id'], registers: row(['kind', 'name', 'icon', 'enabled', 'disabled_reason', 'position', 'trigger_folder_id', 'condition', 'actions', 'archived_at', 'created_at', 'deleted_at']) },
  { table: 'views', key: ['id'], registers: row(['name', 'icon', 'query', 'position', 'created_at', 'deleted_at']) },
  { table: 'assessments', key: ['message_id'], registers: row(['body', 'by_kind', 'by_id', 'created_at', 'deleted_at']), parentMessageColumn: 'message_id' },
  { table: 'digests', key: ['id'], registers: row(['title', 'body_md', 'period_from', 'period_to', 'message_ids', 'by_kind', 'by_id', 'created_at', 'deleted_at']) },
  { table: 'connectors', key: ['id'], registers: row(['name', 'icon', 'access', 'enabled', 'token_hint', 'last_used_at', 'created_at', 'deleted_at']) },
  { table: 'settings', key: ['key'], registers: row(['value']) },
  // Data schema 3 (models.md §4): the model records and the per-model scores of a message.
  ...MODEL_SYNC_TABLES,
] as const satisfies readonly SyncTableSpec[];

export type SyncTableName = (typeof SYNC_TABLES)[number]['table'];

/**
 * Tables that NEVER sync: source_state, local_settings, engine_config, pipeline_jobs, fetch_hints,
 * sync_meta, sync_dirty, sync_seq, stats_dirty, messages_fts, message_vectors, model_vectors (on-device
 * embeddings stay on the device that made them, models.md §4.4), and the column messages.seq.
 * Secrets and media bytes are not in the database at all.
 */

// ── Wire format ────────────────────────────────────────────────────────────────────────────────

export interface RegisterChange {
  /** The register's Hlc after the write. */
  h: Hlc;
  /** Column -> value for EVERY column of the register (never partial). */
  v: Record<string, SqlValue>;
}

/**
 * One row's changed registers.
 *
 * Invariants
 *  - `regs` has at least one entry; keys are register names of the table.
 *  - A change that CREATES a row on the receiver carries all registers of the table. The sender
 *    guarantees this (device: a new row has every register dirty; server: sync.md "Pull").
 *  - Applying the same change twice is a no-op (idempotent).
 */
export interface RowChange {
  t: SyncTableName;
  /** Primary key values joined with '|'. */
  id: string;
  regs: Record<string, RegisterChange>;
}

export interface PushRequest {
  schemaVersion: number;
  changes: RowChange[];
}

export type PushRejectReason =
  | 'missing_base' // partial change for a row the server does not have: the device re-marks the whole row dirty
  | 'clock_skew' // hlc too far in the future
  | 'invalid' // unknown table/register/column or a value violating the schema
  | 'not_writable'; // a device tried to write a server-owned table (connectors)

export interface PushResponse {
  /** Registers written. */
  applied: number;
  /** Registers ignored because the server already had an equal or newer Hlc. */
  stale: number;
  rejected: Array<{ t: SyncTableName; id: string; reason: PushRejectReason }>;
  /** The server's highest sequence number after this push. */
  head: number;
  /** Identity of the replica file. A change means the replica was reset (sync.md "Epoch"). */
  epoch: string;
}

export interface PullRequest {
  /** The device's cursor: it has applied everything with seq <= since. 0 = from the beginning. */
  since: number;
  /** Maximum number of REGISTER changes to return (the server may return fewer). */
  limit: number;
  /**
   * The schema the device speaks (query parameter `schema`). From data schema 3 the device always sends it
   * and the server answers `sync_conflict` when it is absent or different: a tab of an older bundle left
   * open across the roll-out must not be fed rows it cannot store (sync.md §13).
   */
  schemaVersion?: number;
}

export interface PullResponse {
  changes: RowChange[];
  /** New cursor to store once `changes` are applied. */
  next: number;
  /** True when more changes exist after `next`. */
  more: boolean;
  head: number;
  epoch: string;
}

export interface DeviceInfo {
  id: DeviceId;
  name: string;
  platform: 'web' | 'android' | 'ios' | 'desktop';
  createdAt: number;
  lastSyncAt: number | null;
  /** True for the device making the request. */
  current: boolean;
}

/**
 * The device's view of the server sync API. The HTTP implementation lives in
 * packages/core/src/sync/http-transport.ts; tests use an in-process implementation backed by a
 * second database, so the whole protocol is testable without a network.
 *
 * All methods reject with DigesterError: unauthenticated, not_entitled (no sync on this plan),
 * device_limit, sync_conflict (schema), resync_required, rate_limited, fetch_failed (offline).
 */
export interface SyncTransport {
  registerDevice(device: { id: DeviceId; name: string; platform: DeviceInfo['platform'] }): Promise<DeviceInfo[]>;
  listDevices(): Promise<DeviceInfo[]>;
  revokeDevice(id: DeviceId): Promise<DeviceInfo[]>;
  push(request: PushRequest): Promise<PushResponse>;
  pull(request: PullRequest): Promise<PullResponse>;
}

/** Outcome of applying remote changes to a local database (device: pulled; server: pushed). */
export interface ApplyResult {
  applied: number;
  stale: number;
  rejected: PushResponse['rejected'];
  /** Tables touched, for change notifications. */
  tables: SyncTableName[];
  /** Message ids whose rows changed, capped at 500 (beyond that the UI just refetches). */
  messageIds: string[];
}

export type SyncPhase = 'off' | 'idle' | 'pushing' | 'pulling' | 'offline' | 'error' | 'resync';

export interface SyncStatus {
  phase: SyncPhase;
  /** Registers waiting to be pushed. */
  pending: number;
  lastSyncedAt: number | null;
  /** Set when phase = 'error'. */
  error: { code: string; message: string } | null;
}
